import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraEmptyState, SpecraInlineBanner, SpecraMetricStrip, SpecraSectionHeader, SpecraSegmentedControl, SpecraStackedBarList, SpecraText, SpecraTrendChart, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { RefreshCw } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { renderRouterLink } from '../../lib/renderRouterLink';
import { trendPoints } from '../Delivery/CommandCenter/charts';
import { EMPTY_FILTER } from '../Delivery/CommandCenter/filters';
import { ccScope, deliveryTiles, isOwn, matchesTile, programsWords, pursuitBars, pursuitInsight, pursuitsWords, pursuitTile, trendInsight } from './areas';
// SAD-1005 slice 2: Your Areas (the org-wide band, under one scope switch)
// and How It Is Trending. Both read the one summary the page already has.
const SCOPES = [
    {
        value: 'mine',
        label: 'My programs'
    },
    {
        value: 'all',
        label: 'Everything I can see'
    }
];
/** The tiles a row holds: the first 4 that apply (design §3.4). */ const MAX_TILES = 4;
const LOADING_TILES = [
    'On-time delivery',
    'Late now',
    'Waiting on approval'
].map((label)=>({
        id: label,
        label,
        value: null
    }));
export function YourAreas({ summary, hiddenCount, loading, failed, onRetry, scope, onScope, pipeline, matches }) {
    const tiles = useMemo(()=>{
        const extra = [
            pursuitTile(pipeline),
            matchesTile(matches)
        ].filter((t)=>t !== null);
        return [
            ...summary ? deliveryTiles(summary, scope) : [],
            ...extra
        ].slice(0, MAX_TILES);
    }, [
        summary,
        scope,
        pipeline,
        matches
    ]);
    const byId = new Map(tiles.map((t)=>[
            t.id,
            t
        ]));
    const noPrograms = !loading && !failed && !!summary && summary.programs.length === 0;
    const noPursuits = !!pipeline && (!pipeline.hasAccess || pipeline.pursuits === 0);
    // No area to show at all: one honest row per area the viewer lacks.
    const noTiles = noPrograms && (noPursuits || !pipeline) && !matchesTile(matches);
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2",
        "data-testid": Testids.dashboard.areas,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        headingAs: "h2",
                        title: scope === 'all' ? 'Across the Business' : 'Your Areas',
                        variant: "group"
                    }),
                    noTiles ? null : /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                        label: "Scope",
                        onValueChange: onScope,
                        options: SCOPES,
                        size: "sm",
                        testId: Testids.dashboard.scopeSwitch,
                        value: scope
                    })
                ]
            }),
            summary?.mineFallback && scope === 'mine' ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                "data-testid": Testids.dashboard.mineFallback,
                tone: "info",
                variant: "framed",
                children: "You don’t lead, own a team on or hold a role on any program yet, so My programs shows every program you can see."
            }) : null,
            failed ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                action: /*#__PURE__*/ _jsx(SpecraButton, {
                    iconLeft: /*#__PURE__*/ _jsx(RefreshCw, {}),
                    onClick: onRetry,
                    size: "sm",
                    variant: "secondary",
                    children: "Retry"
                }),
                tone: "danger",
                variant: "framed",
                children: "The delivery figures are unavailable. This is a load error, not an empty portfolio."
            }) : noTiles ? /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraEmptyState, {
                        action: /*#__PURE__*/ _jsx(Link, {
                            className: specraButtonVariants({
                                variant: 'secondary',
                                size: 'sm'
                            }),
                            to: "/help",
                            children: "How access works"
                        }),
                        "data-testid": Testids.dashboard.areaEmpty,
                        description: "A program lead adds you on the program’s Roles page, or an admin grants access under Admin › Access. On-time delivery and your deliverables show here once you are on one.",
                        framed: true,
                        size: "compact",
                        title: "Delivery: no programs are shared with you."
                    }),
                    noPursuits ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                        action: /*#__PURE__*/ _jsx(Link, {
                            className: specraButtonVariants({
                                variant: 'secondary',
                                size: 'sm'
                            }),
                            to: "/opportunities",
                            children: "Browse Opportunities"
                        }),
                        "data-testid": Testids.dashboard.areaEmpty,
                        description: "A capture manager adds you to the pursuit team. Decisions and tasks on that pursuit then show in Waiting on You.",
                        framed: true,
                        size: "compact",
                        title: "Pursuits: you are not on a pursuit team."
                    }) : null
                ]
            }) : /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                getItemProps: (m)=>({
                        'data-item-id': m.id,
                        'data-kpi-tone': byId.get(m.id)?.kpiTone ?? ''
                    }),
                getItemTestId: ()=>Testids.dashboard.areaTile,
                loading: loading && tiles.length === 0,
                metrics: tiles.length > 0 ? tiles : LOADING_TILES,
                renderLink: renderRouterLink
            }),
            hiddenCount > 0 ? /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                "data-testid": Testids.dashboard.hiddenNote,
                size: "meta",
                tone: "muted",
                children: [
                    hiddenCount.toLocaleString(),
                    ' ',
                    hiddenCount === 1 ? 'program in your organisation is' : 'programs in your organisation are',
                    ' ',
                    "not visible to you."
                ]
            }) : null
        ]
    });
}
/** The pursuits chart's two segments: open, and closed in the last 12 months. */ const PURSUIT_SEGMENTS = [
    {
        key: 'open',
        label: 'Open',
        tone: 'series'
    },
    {
        key: 'closed',
        label: 'Closed in the last 12 months',
        tone: 'pending'
    }
];
function PursuitsByStage({ pipeline, scope }) {
    const bars = pursuitBars(pipeline);
    const words = pursuitsWords(pipeline, scope);
    return /*#__PURE__*/ _jsx(SpecraStackedBarList, {
        "data-testid": Testids.dashboard.pursuits,
        description: `${words[0].toUpperCase()}${words.slice(1)} · now`,
        emptyMessage: "No pursuit is open or closed in the last 12 months.",
        getRowTestId: ()=>Testids.dashboard.pursuitStage,
        insight: pursuitInsight(pipeline),
        renderLink: renderRouterLink,
        rows: bars.map((b)=>({
                id: b.id,
                label: b.label,
                values: {
                    [b.open ? 'open' : 'closed']: b.count
                },
                hrefs: {
                    [b.open ? 'open' : 'closed']: b.href
                },
                value: b.end,
                valueTone: b.open ? undefined : 'muted',
                hint: 'Click to list them'
            })),
        segments: PURSUIT_SEGMENTS,
        title: "Pursuits by stage",
        valueLabel: "Pursuits · value on record"
    });
}
export function HowItIsTrending({ summary, scope, pipeline }) {
    const filter = useMemo(()=>({
            ...EMPTY_FILTER,
            scope: ccScope(scope)
        }), [
        scope
    ]);
    const points = useMemo(()=>trendPoints(summary, filter), [
        summary,
        filter
    ]);
    const delivery = !!summary && summary.programs.length > 0;
    const pursuits = !!pipeline?.hasAccess && pipeline.pursuits > 0;
    if (!delivery && !pursuits) return null;
    const onTime = summary?.kpis.find((k)=>k.id === 'on_time');
    const target = Number.parseInt(onTime?.target ?? '100', 10);
    const insight = summary ? trendInsight(summary) : '';
    const words = summary ? programsWords(summary.programs.length, isOwn(scope, summary)) : '';
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-baseline justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        headingAs: "h2",
                        title: "How It Is Trending",
                        variant: "group"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: "Click a point or a stage to open its list"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: delivery && pursuits ? 'grid gap-4 lg:grid-cols-2' : 'grid gap-4',
                children: [
                    delivery ? /*#__PURE__*/ _jsx(SpecraTrendChart, {
                        "data-testid": Testids.dashboard.trend,
                        description: `${words[0].toUpperCase()}${words.slice(1)} · 12 months · the on-time tile’s rule`,
                        emptyMessage: "Nothing came due in the last 12 months, so there is no on-time rate yet. It starts counting at the first approval or the first overdue deliverable.",
                        getPointTestId: ()=>Testids.dashboard.trendPoint,
                        insight: insight ? /*#__PURE__*/ _jsx("span", {
                            "data-testid": Testids.dashboard.trendInsight,
                            children: insight
                        }) : undefined,
                        labelEvery: 2,
                        points: points,
                        renderLink: renderRouterLink,
                        seriesLabel: "On-time delivery",
                        target: Number.isFinite(target) ? {
                            value: target,
                            label: `Target: ${target}%`
                        } : undefined,
                        title: "On-time delivery by month"
                    }) : null,
                    pursuits && pipeline ? /*#__PURE__*/ _jsx(PursuitsByStage, {
                        pipeline: pipeline,
                        scope: scope
                    }) : null
                ]
            })
        ]
    });
}
